import { type ActionCard, useStores } from '@littlebird/sdk';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { format } from 'date-fns/format';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { XStack, YStack } from '@/components/ui';
import { ActionCardItem } from '@/features/action-cards/components/ActionCardItem';
import {
  USER_DID_SEND_MESSAGE_EVENT,
  localChatEventsBus,
} from '@/features/chat/chat.events';
import { useChromelessSurface } from '@/hooks/useChromelessSurface';
import { cn } from '@/lib/utils';
import { uiStore } from '@/stores/ui.store';

import { AgentsAttentionStrip } from './agents-attention-strip';
import { AgentAvatar } from './agents-avatar';
import { birdLookFromName } from './agents-bird.look';
import { AgentComposer } from './agents-composer';
import { useAgentPendingCards, useArrowKeysCycleAgents } from './agents.hooks';
import { type AgentEntry, agentsStore } from './agents.store';
import {
  formatDayLabel,
  groupByDay,
  groupPendingCardsByAgent,
  isPendingAgentCard,
} from './agents.utils';

export const AgentsAttentionPage = observer(() => {
  useChromelessSurface();
  const navigate = useNavigate();
  const { agent: selectedAgentId } = useSearch({
    from: '/_authenticated/agents/attention',
  });
  const pendingCards = useAgentPendingCards();
  const groups = groupPendingCardsByAgent(
    pendingCards,
    agentsStore.agents,
    (card) => (card.chatId ? agentsStore.getByChatId(card.chatId) : undefined)
  );
  const selectedGroup = groups.find(
    (group) => group.agent.agentId === selectedAgentId
  );
  const cards = selectedGroup?.cards ?? pendingCards;
  const waitingCount = cards.filter(isPendingAgentCard).length;
  const composerAgent =
    selectedGroup?.agent ?? (groups.length === 1 ? groups[0].agent : undefined);
  const sendAskUserAnswersRef = useRef<() => void>(() => {});
  const composerChatId = composerAgent?.chatId;
  useEffect(() => {
    if (!composerChatId) return;
    const listener = localChatEventsBus.on(
      USER_DID_SEND_MESSAGE_EVENT,
      ({ data }) => {
        if (data.threadId !== composerChatId) return;
        void navigate({ to: `/chats/${composerChatId}` });
      }
    );
    return () => listener.unsubscribe();
  }, [composerChatId, navigate]);
  const selectAgent = useCallback(
    (agentId: string | undefined) =>
      void navigate({
        to: '/agents/attention',
        search: { agent: agentId },
        replace: true,
      }),
    [navigate]
  );
  const visibleAgentIdsKey = groups
    .filter(
      (group) =>
        group.cards.length > 0 || group.agent.agentId === selectedAgentId
    )
    .map((group) => group.agent.agentId)
    .join(',');
  const visibleAgentIds = useMemo(
    () => visibleAgentIdsKey.split(',').filter(Boolean),
    [visibleAgentIdsKey]
  );
  useArrowKeysCycleAgents(
    visibleAgentIds,
    selectedGroup?.agent.agentId,
    selectAgent,
    visibleAgentIds.length > 0
  );

  return (
    <YStack className="h-full min-w-0 flex-1">
      <YStack className="relative z-10 shrink-0 pt-12">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-full h-6 bg-gradient-to-b from-background-subtle to-transparent"
        />
        <YStack className="mx-auto w-full max-w-3xl gap-6 px-3 md:px-6">
          <XStack className="items-baseline justify-between gap-4">
            <span className="text-lg font-medium text-foreground">
              Needs attention
            </span>
            {waitingCount > 0 && (
              <span className="text-sm text-foreground-muted">
                {`${waitingCount} ${waitingCount === 1 ? 'action' : 'actions'} waiting on you`}
              </span>
            )}
          </XStack>
        </YStack>
        {groups.length > 1 && (
          <YStack className="relative mt-6 w-full">
            <AgentsAttentionStrip
              groups={groups}
              totalCount={pendingCards.length}
              selectedAgentId={selectedGroup?.agent.agentId}
              onSelect={selectAgent}
            />
          </YStack>
        )}
      </YStack>
      <YStack className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <YStack className="mx-auto w-full max-w-3xl shrink-0 px-3 pb-10 pt-6 md:px-6">
          <YStack className="gap-6">
            {cards.length === 0 ? (
              <AttentionEmptyState agent={selectedGroup?.agent} />
            ) : (
              <YStack className="gap-10">
                {groupByDay(cards, (card) => card.createdAt).map(
                  ({ day, items }) => (
                    <YStack key={day.getTime()} className="gap-5">
                      <XStack className="items-center gap-3">
                        <span className="text-xs font-medium text-foreground-muted">
                          {formatDayLabel(day)}
                        </span>
                        <span className="h-px flex-1 bg-border" />
                      </XStack>
                      <YStack className="gap-3">
                        {items.map((card) => (
                          <AttentionCard
                            key={card.id}
                            card={card}
                            showAgent={selectedGroup === undefined}
                          />
                        ))}
                      </YStack>
                    </YStack>
                  )
                )}
              </YStack>
            )}
          </YStack>
        </YStack>
      </YStack>
      {composerAgent?.chatId && (
        <YStack className="mx-auto w-full max-w-3xl shrink-0 px-3 pb-4 pt-2 md:px-6">
          <AgentComposer
            key={composerAgent.chatId}
            threadId={composerAgent.chatId}
            sendAskUserAnswersRef={sendAskUserAnswersRef}
            setIsDraggingFile={(value) => uiStore.setIsDraggingFile(value)}
          />
        </YStack>
      )}
    </YStack>
  );
});

const AttentionEmptyState = observer(
  ({ agent: selectedAgent }: { agent: AgentEntry | undefined }) => {
    const navigate = useNavigate();
    const { drafts } = useStores();
    const agent = selectedAgent ?? agentsStore.agents[0];

    const tryIt = () => {
      if (!agent?.chatId) return;
      drafts.createDraft({
        draftId: agent.chatId,
        content: 'Draft an email for me to review before it goes out.',
      });
      void navigate({ to: `/chats/${agent.chatId}` });
    };

    return (
      <YStack className="gap-5 pb-2 pt-6">
        <YStack className="gap-1">
          <span className="text-sm font-medium text-foreground">
            {selectedAgent
              ? `Nothing from ${selectedAgent.name}`
              : 'Nothing waiting on you'}
          </span>
          <span className="max-w-md text-xs.5 text-foreground-muted">
            {selectedAgent
              ? `When ${selectedAgent.name} drafts an email, an event, or a message, it lands here for your OK before it goes out.`
              : 'When an agent drafts an email, an event, or a message, it lands here for your OK before it goes out.'}
          </span>
        </YStack>

        <div
          aria-hidden="true"
          className="w-full select-none rounded-2xl border border-border-strong bg-surface p-5 text-left opacity-60"
        >
          <XStack className="items-center gap-2.5">
            <AgentAvatar
              bird={agent?.bird ?? birdLookFromName('Sam')}
              className="size-7"
            />
            <span className="text-sm font-medium text-foreground">
              {agent?.name ?? 'Sam'}
            </span>
            <span className="text-xs text-foreground-muted">
              Send email · Gmail
            </span>
            <span className="ml-auto rounded-md bg-surface-active px-1.5 py-px text-[10px] leading-4 text-foreground-muted">
              Example
            </span>
          </XStack>
          <YStack className="mt-4 gap-1.5">
            <span className="text-sm text-foreground">
              Re: Launch brief for Jordan
            </span>
            <span className="text-sm text-foreground-muted">
              Hi Jordan, here's the brief we discussed, with the two timing
              questions flagged at the top…
            </span>
          </YStack>
          <XStack className="mt-5 justify-end gap-2">
            <span className="rounded-lg px-3 py-1 text-xs text-foreground-muted">
              Dismiss
            </span>
            <span className="rounded-lg bg-accent px-3 py-1 text-xs text-accent-foreground">
              Send
            </span>
          </XStack>
        </div>

        {agent?.chatId && (
          <button
            type="button"
            onClick={tryIt}
            className="self-start text-xs.5 text-foreground-muted hover:text-foreground focus-ring"
          >
            Ask {agent.name} to draft one →
          </button>
        )}
      </YStack>
    );
  }
);

const AttentionCard = observer(
  ({ card, showAgent }: { card: ActionCard; showAgent: boolean }) => {
    const navigate = useNavigate();
    const agent = card.chatId
      ? agentsStore.getByChatId(card.chatId)
      : undefined;
    const agentName = agent?.name ?? 'Agent';
    const timestampLabel = format(card.createdAt, 'h:mm a');

    const chatId = card.chatId;
    const openChat = chatId
      ? () => void navigate({ to: `/chats/${chatId}` })
      : undefined;
    const tagClassName =
      'shrink-0 whitespace-nowrap rounded-md bg-surface-hover px-2 py-0.5 text-[0.6875rem] leading-4 text-foreground-muted';
    const tag = openChat ? (
      <button
        type="button"
        title={`Open ${agentName}`}
        className={cn(
          tagClassName,
          'hover:bg-surface-active hover:text-foreground focus-ring'
        )}
        onClick={openChat}
      >
        {timestampLabel}
      </button>
    ) : (
      <span className={tagClassName}>{timestampLabel}</span>
    );

    return (
      <YStack className="gap-1.5">
        {showAgent && (
          <button
            type="button"
            disabled={!openChat}
            className="flex items-center gap-2 self-start rounded-lg px-1 py-0.5 text-sm font-medium text-foreground enabled:hover:bg-surface-hover focus-ring"
            onClick={openChat}
          >
            <AgentAvatar
              bird={agent?.bird ?? birdLookFromName(agentName)}
              className="size-5"
            />
            {agentName}
          </button>
        )}
        <ActionCardItem card={card} sourceChatTag={tag} />
      </YStack>
    );
  }
);
